/** Page détail employé - infos, badge, planning prévu vs réalisé */

import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Trash2 } from 'lucide-react';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useEmployee, useRemoveBadge } from '../hooks/useEmployees';
import { useEmployeeSummary } from '../hooks/useTimeLogs';
import { formatDelta } from '../lib/utils';

type ViewMode = 'week' | 'month';

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const employeeId = Number(id);

  const { data: employee, isLoading } = useEmployee(employeeId);
  const removeBadge = useRemoveBadge();

  // Période affichée
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [referenceDate, setReferenceDate] = useState(new Date());

  const { dateFrom, dateTo } = useMemo(() => {
    if (viewMode === 'week') {
      return {
        dateFrom: format(startOfWeek(referenceDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        dateTo: format(endOfWeek(referenceDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      };
    }
    return {
      dateFrom: format(startOfMonth(referenceDate), 'yyyy-MM-dd'),
      dateTo: format(endOfMonth(referenceDate), 'yyyy-MM-dd'),
    };
  }, [viewMode, referenceDate]);

  const { data: summary, isLoading: summaryLoading } = useEmployeeSummary(
    employeeId,
    dateFrom,
    dateTo
  );

  const handleRemoveBadge = () => {
    if (window.confirm('Dissocier le badge de cet employé ?')) {
      removeBadge.mutate(employeeId);
    }
  };

  // Navigation de période
  const shiftPeriod = (direction: number) => {
    const d = new Date(referenceDate);
    if (viewMode === 'week') d.setDate(d.getDate() + direction * 7);
    else d.setMonth(d.getMonth() + direction);
    setReferenceDate(d);
  };

  if (isLoading) return <p className="text-sm text-gray-400 p-6">Chargement...</p>;
  if (!employee) return <p className="text-sm text-red-500 p-6">Employé introuvable</p>;

  return (
    <div>
      <Header
        title={`${employee.first_name} ${employee.last_name}`}
        action={
          <Button variant="ghost" size="sm" onClick={() => navigate('/employees')}>
            <ArrowLeft size={14} className="mr-1" /> Retour
          </Button>
        }
      />

      {/* Informations */}
      <Card title="Informations" className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div>
            <span className="text-gray-500">Email</span>
            <p className="text-gray-900">{employee.email || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500">Département</span>
            <p className="text-gray-900">{employee.department || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500">Site</span>
            <p className="text-gray-900">{employee.site_name || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500">Statut</span>
            <p>
              <Badge variant={employee.is_active ? 'success' : 'danger'}>
                {employee.is_active ? 'Actif' : 'Inactif'}
              </Badge>
            </p>
          </div>
          <div className="col-span-2">
            <span className="text-gray-500">Badge</span>
            <div className="flex items-center gap-2 mt-1">
              {employee.badge_uid ? (
                <>
                  <span className="font-mono text-sm text-gray-900">{employee.badge_uid}</span>
                  <Button variant="ghost" size="sm" onClick={handleRemoveBadge}>
                    <Trash2 size={12} className="text-red-500 mr-1" /> Dissocier
                  </Button>
                </>
              ) : (
                <>
                  <Badge variant="warning">Aucun badge</Badge>
                  <Link to={`/badge-enroll?employee=${employeeId}`}>
                    <Button variant="secondary" size="sm">
                      <CreditCard size={12} className="mr-1" /> Enrôler
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Résumé pointages - prévu vs réalisé */}
      <Card
        title="Pointages"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => shiftPeriod(-1)}>
              &larr;
            </Button>
            <span className="text-xs text-gray-600">
              {format(new Date(dateFrom), 'dd MMM', { locale: fr })} —{' '}
              {format(new Date(dateTo), 'dd MMM yyyy', { locale: fr })}
            </span>
            <Button variant="ghost" size="sm" onClick={() => shiftPeriod(1)}>
              &rarr;
            </Button>
            <div className="flex border border-gray-200 rounded-md ml-2">
              <button
                onClick={() => setViewMode('week')}
                className={`px-2 py-1 text-xs ${viewMode === 'week' ? 'bg-accent text-white' : 'text-gray-600'} rounded-l-md`}
              >
                Semaine
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-2 py-1 text-xs ${viewMode === 'month' ? 'bg-accent text-white' : 'text-gray-600'} rounded-r-md`}
              >
                Mois
              </button>
            </div>
          </div>
        }
      >
        {summaryLoading ? (
          <p className="text-sm text-gray-400">Chargement...</p>
        ) : !summary ? (
          <p className="text-sm text-gray-400">Aucune donnée</p>
        ) : (
          <>
            {/* Tableau détaillé par jour */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Jour</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Prévu</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Pointé</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Réalisé</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.days.map((day, i) => (
                    <tr
                      key={day.date}
                      className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'} ${!day.is_working_day ? 'opacity-50' : ''}`}
                    >
                      <td className="px-3 py-2 text-gray-700">
                        <span className="font-medium">{day.day_label}</span>
                        <span className="text-gray-400 ml-1 text-xs">
                          {format(new Date(day.date), 'dd/MM')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {day.is_working_day
                          ? `${day.scheduled_start} - ${day.scheduled_end}`
                          : 'Repos'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {day.actual_first_in
                          ? `${day.actual_first_in} → ${day.actual_last_out || '...'}`
                          : day.is_working_day
                            ? '—'
                            : ''}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {day.actual_hours > 0 ? `${day.actual_hours}h` : day.is_working_day ? '0h' : ''}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {day.is_working_day && (
                          <span
                            className={
                              day.delta_hours > 0
                                ? 'text-green-600'
                                : day.delta_hours < 0
                                  ? 'text-red-600'
                                  : 'text-gray-400'
                            }
                          >
                            {formatDelta(day.delta_hours)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totaux */}
            <div className="flex justify-end gap-6 mt-3 pt-3 border-t border-gray-100 text-sm">
              <div className="text-gray-500">
                Prévu : <span className="font-medium text-gray-900">{summary.total_scheduled_hours}h</span>
              </div>
              <div className="text-gray-500">
                Réalisé : <span className="font-medium text-gray-900">{summary.total_actual_hours}h</span>
              </div>
              <div
                className={
                  summary.total_delta_hours > 0
                    ? 'text-green-600 font-semibold'
                    : summary.total_delta_hours < 0
                      ? 'text-red-600 font-semibold'
                      : 'text-gray-400'
                }
              >
                {formatDelta(summary.total_delta_hours)}
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
